var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { config, envSupport, setConfig } from './config';
import { SHMError } from './error';
import { addListener, dispatchEvent } from './event';
import { logger } from './logger';
import { get, post, inferContentType } from './request';
const logCategory = 'interface:api';
let defaultHeaders = {};
function composeDefaultHeaders() {
    defaultHeaders = {};
    if (config.platform) {
        defaultHeaders['X-Platform'] = config.platform;
    }
    if (config.app) {
        defaultHeaders['X-App'] = `${config.app}`;
    }
    if (config.appVersion) {
        defaultHeaders['X-App-Version'] = config.appVersion;
    }
    if (config.token) {
        defaultHeaders.Authorization = `Bearer ${config.token}`;
    }
    if (config.tk) {
        defaultHeaders['X-Tk'] = config.tk;
    }
    if (config.sessionID) {
        defaultHeaders['X-Session-ID'] = config.sessionID;
    }
    Object.assign(defaultHeaders, config.headers);
}
// side effects here need be prevented from tree shaking
composeDefaultHeaders();
addListener('configUpdated', composeDefaultHeaders);
const composeOptions = (opts) => {
    const _a = opts || {}, { headers = {} } = _a, restOpts = __rest(_a, ["headers"]);
    return Object.assign({ headers: Object.assign(Object.assign({}, defaultHeaders), headers) }, restOpts);
};
function apiRequest(apiCreator, codeExtractor) {
    const usedToken = config.token;
    return apiCreator()
        .then((res) => {
        dispatchEvent('response', res);
        const code = codeExtractor(res);
        if (code === 16 /* Code.UNAUTHENTICATED */) {
            // token invalid
            if (config.token && config.token === usedToken) {
                logger.info('clear invalid token', logCategory);
                setConfig({ token: '' });
                dispatchEvent('tokenInvalid');
            }
        }
        if (code > 0) {
            throw res;
        }
        return res;
    })
        .catch((err) => {
        // TODO: move this into interfaceAPI
        const code = codeExtractor(err) || 2 /* Code.UNKNOWN */;
        dispatchEvent('error', SHMError.create({
            code,
            extra: err,
        }));
        throw err;
    });
}
const statusCodeExtractor = (res) => {
    var _a;
    const { headers } = res;
    const code = Number((_a = headers === null || headers === void 0 ? void 0 : headers.get('grpc-status')) !== null && _a !== void 0 ? _a : 2 /* Code.UNKNOWN */);
    return Number.isNaN(code) ? 2 /* Code.UNKNOWN */ : code;
};
const errorMessageExtractor = (res) => {
    const { headers } = res;
    const msg = headers === null || headers === void 0 ? void 0 : headers.get('grpc-message');
    return typeof msg === 'string' ? decodeURIComponent(msg) : '';
};
const requestIDExtractor = (res) => {
    const { headers } = res;
    return headers === null || headers === void 0 ? void 0 : headers.get('x-request-id');
};
const logFreeAPIPaths = [
    '/report.Default/ReportLogs',
    '/report.Default/ReportEvents', // too verbose, report events has its own log
];
function composeHeaders(params, options) {
    const headers = {};
    headers['Content-Type'] = inferContentType(params);
    const { isRequestEncrypted, isReplyEncrypted } = options;
    if (isRequestEncrypted) {
        headers['X-Request-Encrypted'] = '1';
    }
    if (isReplyEncrypted) {
        headers['X-Reply-Encrypted'] = '1';
    }
    if (isRequestEncrypted || isReplyEncrypted) {
        headers['X-Encrypt-Method'] = `${config.crypter.method}`;
    }
    return headers;
}
function composeBody(params, isRequestEncrypted) {
    const str = JSON.stringify(params);
    if (!isRequestEncrypted) {
        return Promise.resolve(str);
    }
    return config.crypter.encrypt(str);
}
function decryptReply(res, isReplyEncrypted, isLogEnabled, path) {
    const { data, headers } = res;
    // decrypt if needed
    const encrypted = isReplyEncrypted && Number(headers === null || headers === void 0 ? void 0 : headers.get('X-Reply-Encrypted'));
    if (!encrypted) {
        return res;
    }
    if (isLogEnabled) {
        logger.debug('request reply encrypted', logCategory, {
            path,
            request_id: requestIDExtractor(res),
            encrypted_data: data,
        });
    }
    return config.crypter.decrypt(data).then((decrypted) => {
        const decryptedData = JSON.parse(decrypted);
        return Object.assign(Object.assign({}, res), { data: decryptedData });
    });
}
function handleError(res, isLogEnabled, path) {
    const code = statusCodeExtractor(res);
    const msg = errorMessageExtractor(res);
    const requestID = requestIDExtractor(res);
    if (isLogEnabled) {
        logger.debug(`request failed`, logCategory, {
            path,
            request_id: requestID,
            code,
            msg,
        });
    }
    throw SHMError.create({
        code,
        msg,
        extra: Object.assign(Object.assign({}, res === null || res === void 0 ? void 0 : res.data), { request_id: requestID }),
    });
}
/**
 * wrap API based on interface definition
 * @param path
 * @param params
 * @param replyDecoder
 * @param options
 */
export function interfaceAPI(path, params = {}, replyDecoder = undefined, options = undefined) {
    // skip log if it's not enabled and not a ReportLogs api (which would cause infinite loop)
    const isLogEnabled = config.log && !logFreeAPIPaths.includes(path);
    if (isLogEnabled) {
        logger.debug('sending request', logCategory, {
            path,
            data: JSON.stringify(params),
        });
    }
    // check auth token
    if (!(options === null || options === void 0 ? void 0 : options.isAuthFree) && !config.token) {
        logger.warn(`request failed: no token for api ${path}`, logCategory, {
            path,
            data: JSON.stringify(params),
        });
        return Promise.reject(SHMError.create({
            msg: 'no token',
            code: 16 /* Code.UNAUTHENTICATED */,
        }));
    }
    // check options
    const isCrypterAvailable = !!config.crypter;
    const isRequestEncrypted = isCrypterAvailable && (options === null || options === void 0 ? void 0 : options.isRequestEncrypted);
    const isReplyEncrypted = isCrypterAvailable && (options === null || options === void 0 ? void 0 : options.isReplyEncrypted);
    return composeBody(params, isRequestEncrypted)
        .then((body) => apiRequest(() => {
        var _a;
        return ((_a = envSupport.post) !== null && _a !== void 0 ? _a : post)(`${config.apiServer}${path}`, body, composeOptions({
            headers: composeHeaders(params, {
                isRequestEncrypted,
                isReplyEncrypted,
            }),
            signal: options === null || options === void 0 ? void 0 : options.signal,
        }));
    }, statusCodeExtractor))
        .then((res) => decryptReply(res, isReplyEncrypted, isLogEnabled, path))
        .then((res) => {
        var _a;
        const { data } = res;
        if (isLogEnabled) {
            logger.debug('request success', logCategory, {
                path,
                request_id: requestIDExtractor(res),
                data: JSON.stringify(data),
            });
        }
        if (!replyDecoder) {
            return data;
        }
        return replyDecoder.decode((_a = data) !== null && _a !== void 0 ? _a : {});
    })
        .catch((res) => handleError(res, isLogEnabled, path));
}
// TODO: remove below legacy methods
const legacyStatusCodeExtractor = (res) => {
    var _a;
    const code = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.code;
    return typeof code === 'number' ? code : 2 /* Code.UNKNOWN */;
};
const legacyResponseDataExtractor = (res) => {
    var _a;
    return (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
};
/**
 * @deprecated: 2021-01-11 legacy api support is being removed, please upgrade to use interfaceAPI
 * @param path
 * @param params
 * @param opts
 */
export const getAPI = (path, params, opts) => {
    return apiRequest(() => get(`${config.apiServer}${path}`, params, composeOptions(opts)), legacyStatusCodeExtractor).then(legacyResponseDataExtractor);
};
/**
 * @deprecated: 2021-01-11 legacy api support is being removed, please upgrade to use interfaceAPI
 * @param path
 * @param params
 * @param opts
 */
export const postAPI = (path, params, opts) => {
    return apiRequest(() => post(`${config.apiServer}${path}`, params, composeOptions(opts)), legacyStatusCodeExtractor).then(legacyResponseDataExtractor);
};
